import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, Heart, Image as ImageIcon, MessageSquare } from 'lucide-react';

const titleClampStyle = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
};

const bodyClampStyle = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
};

const BlogCardMedia = React.memo(({ blog, compact }) => {
  const [hasError, setHasError] = useState(false);

  if (compact) {
    return (
      <div className="h-full w-full">
        {!hasError ? (
          <img
            src={blog.image}
            alt={blog.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            referrerPolicy="no-referrer"
            loading="lazy"
            onError={() => setHasError(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#ede4fb] to-[#f8e8df] text-[#9b55dd]">
            <ImageIcon className="h-6 w-6" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative h-40 overflow-hidden rounded-t-[26px] bg-[#f3effa]">
      {!hasError ? (
        <img
          src={blog.image}
          alt={blog.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setHasError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#ede4fb] to-[#f8e8df] text-[#9b55dd]">
          <div className="flex flex-col items-center gap-2">
            <ImageIcon className="h-8 w-8" />
            <span className="text-xs font-semibold uppercase tracking-[0.16em]">Blog Image</span>
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#120d1d]/18 via-transparent to-transparent" />
      <span className="absolute left-4 top-4 rounded-full bg-gradient-to-r from-[#7f3df1] to-[#ff6f45] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white shadow-md">
        {blog.category}
      </span>
    </div>
  );
})

const BlogCard = React.memo(({ blog, compact = false }) => {
  const authorName = typeof blog.author === 'string' ? blog.author : blog.author?.fullName || 'Race Next Step';
  const authorInitials = authorName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  if (compact) {
    return (
      <article className="group flex gap-3 py-3">
        <Link to={`/blogs/${blog.slug}`} className="shrink-0">
          <div className="relative h-16 w-20 overflow-hidden rounded-lg bg-[#f3effa]">
            <BlogCardMedia blog={blog} compact />
          </div>
        </Link>
        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <Link to={`/blogs/${blog.slug}`}>
            <h3 className="text-sm font-bold leading-snug text-[#1f1730]" style={titleClampStyle}>
              {blog.title}
            </h3>
          </Link>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[10px] font-semibold text-[#9a51a0]">{blog.category}</span>
            <span className="text-[10px] text-[#807290]">{blog.readTime}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-[10px] font-semibold text-[#766a86]">
            <span className="inline-flex items-center gap-1 text-[#db4f89]"><Heart className="h-3 w-3" />{blog.likes}</span>
            <span className="inline-flex items-center gap-1 text-[#8653df]"><MessageSquare className="h-3 w-3" />{blog.comments}</span>
            <span className="inline-flex items-center gap-1 text-[#4b79d9]"><Eye className="h-3 w-3" />{blog.views}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group relative flex min-h-[420px] flex-col overflow-hidden rounded-[26px] border border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(15,23,42,0.08)]">
      <Link to={`/blogs/${blog.slug}`} className="block">
        <BlogCardMedia blog={blog} compact={false} />
      </Link>

      <div className="relative flex flex-1 flex-col px-4 pb-4 pt-3.5">
        <Link to={`/blogs/${blog.slug}`} className="block">
          <h3 className="text-[1.08rem] font-bold leading-[1.3] text-[#1f1730] sm:text-[1.14rem]" style={titleClampStyle}>
            {blog.title}
          </h3>
        </Link>
        <p className="mt-2 flex-1 text-[0.9rem] leading-6 text-[#625470]" style={bodyClampStyle}>
          {blog.description}
        </p>

        <div className="mt-auto flex flex-nowrap items-center gap-2 pt-3 text-[0.76rem] font-semibold text-[#766a86]">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fff4f8] px-2.5 py-1 text-[#db4f89]">
            <Heart className="h-3.5 w-3.5" />
            {blog.likes}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f5f1ff] px-2.5 py-1 text-[#8653df]">
            <MessageSquare className="h-3.5 w-3.5" />
            {blog.comments}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#edf4ff] px-2.5 py-1 text-[#4b79d9]">
            <Eye className="h-3.5 w-3.5" />
            {blog.views}
          </span>
        </div>

        <div className="mt-3.5 flex items-center justify-between border-t border-gray-100 pt-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white shadow-sm">
              {authorInitials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-[0.85rem] font-semibold text-[#241b37]">{authorName}</p>
              <p className="text-[0.72rem] text-[#807290]">
                {blog.publishDate} | {blog.readTime}
              </p>
            </div>
          </div>

          <Link
            to={`/blogs/${blog.slug}`}
            className="inline-flex shrink-0 items-center gap-1 text-[0.85rem] font-semibold text-[#a03de0] transition-colors hover:text-[#ff6f45]"
          >
            Read More
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </article>
  );
});

export default BlogCard;
